/**
 * Per-contact Google Ads upload status pill for the Qualified / Converted
 * tables. Shown only when the location is in direct-upload mode.
 *
 * Fed by `useContactGoogleAdsStatus`, which batches the lookup per page —
 * we just receive the row (or undefined for "no upload yet") and render the
 * right color + a tooltip with the details (validateOnly, timestamps, errors).
 */

import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatRelativeTime } from '@/lib/ghl-qualifier'
import type { ContactGoogleAdsStatus } from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly row?: ContactGoogleAdsStatus | null
}

/**
 * Status → Tailwind class pair. Anything unknown falls back to neutral gray so
 * a future status string introduced server-side doesn't crash the cell.
 */
const COLORS: Record<string, { bg: string; text: string; label?: string }> = {
  uploaded: { bg: 'bg-green-100', text: 'text-green-800' },
  validated: { bg: 'bg-blue-100', text: 'text-blue-800' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  processing: { bg: 'bg-slate-100', text: 'text-slate-700' },
  failed: { bg: 'bg-red-100', text: 'text-red-800' },
  skipped_no_identifier: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'no id' },
}

const NEUTRAL = { bg: 'bg-slate-100', text: 'text-slate-700' }

export function UploadStatusBadge({ row }: Props) {
  if (!row) {
    return <span className="text-muted-foreground">—</span>
  }

  const style = COLORS[row.status] ?? NEUTRAL
  const baseLabel = style.label ?? row.status
  const isValidate = row.validate_only === true

  // Tooltip body — readable summary the operator can scan without leaving the table.
  const tipLines: string[] = [`status: ${row.status}`]
  if (isValidate) tipLines.push('validateOnly = true (no real conversion recorded)')
  if (row.uploaded_at) {
    tipLines.push(`uploaded at: ${new Date(row.uploaded_at).toLocaleString()}`)
  }
  if (row.attempt_count != null && row.attempt_count > 0) {
    tipLines.push(`attempts: ${row.attempt_count}`)
  }
  if (row.error) tipLines.push(`error: ${row.error}`)
  tipLines.push(`updated: ${formatRelativeTime(row.updated_at)}`)

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className={`${style.bg} ${style.text} hover:${style.bg} text-[10px] py-0 px-1.5 cursor-help`}
          >
            {baseLabel}
            {isValidate ? ' (test)' : ''}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap text-xs">
          {tipLines.join('\n')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
